import { Add, Check, Close, Delete, Link } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
  deDE,
} from '@mui/x-data-grid';
import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { ILegalRole } from '../Interfaces/ILegalRole';
import { ILegalRole_canInvite } from '../Interfaces/ILegalRole_canInvite';
import { ILegalTransactionCustomId } from '../Interfaces/ILegalTransactionCustomId';
import { LegalRoleEditDialog } from './Edit/LegalRole/LegalRoleEditDialog';
import { AssignLegalClientsDialog } from './LegalClient/AssignLegalClientsDialog';
import { LegalClientsInvitedOverview } from './LegalClient/LegalClientsInvitedOverview';

interface IProps {
  idLegalTransaction?: number;
  setLegalRoles?: React.Dispatch<React.SetStateAction<ILegalRole[]>>;
  legalRoleCanInviteMap?: ILegalRole_canInvite[];
  setLegalRoleCanInviteMap?: React.Dispatch<
    React.SetStateAction<ILegalRole_canInvite[]>
  >;
  legalRoles: ILegalRole[];
  takenLegalClients: ILegalClient[];
  setTakenLegalClients: React.Dispatch<React.SetStateAction<ILegalClient[]>>;
  setCustomIds?: React.Dispatch<
    React.SetStateAction<ILegalTransactionCustomId[]>
  >;
}

export const LegalTransactionClientsOverview: React.FC<IProps> = ({
  idLegalTransaction,
  setCustomIds,
  setLegalRoles,
  legalRoleCanInviteMap,
  setLegalRoleCanInviteMap,
  legalRoles,
  takenLegalClients,
  setTakenLegalClients,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const onRemove = useCallback(
    (clientId: number) => {
      setTakenLegalClients(prev => [
        ...prev.filter(client => client.idLegalClient !== clientId),
      ]);
    },
    [setTakenLegalClients]
  );

  const onChange = useCallback(
    (clientId: number, key: string, value: boolean | number) => {
      setTakenLegalClients(prev => [
        ...prev.map(client =>
          client.idLegalClient === clientId
            ? { ...client, [key]: value }
            : client
        ),
      ]);
    },
    [setTakenLegalClients]
  );

  const onTakeClients = useCallback(
    (newClients: ILegalClient[]) => {
      setTakenLegalClients(
        newClients.map(c => ({
          ...c,
          LegalClientGroups: c.LegalClientGroups ?? [],
          idLegalRole: c.idLegalRole || legalRoles[0]?.idLegalRole,
          enableInvoiceReminding:
            c.enableInvoiceReminding !== undefined
              ? c.enableInvoiceReminding
              : false,
          enableCertificateReminding:
            c.enableCertificateReminding !== undefined
              ? c.enableCertificateReminding
              : true,
        }))
      );
    },
    [setTakenLegalClients, legalRoles]
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'idLegalClient', headerName: 'Nr.', flex: 1 },
      {
        field: 'idLegalRole',
        headerName: 'Rolle',
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams<ILegalClient>) =>
          legalRoles.length ? (
            <TextField
              select
              fullWidth
              size='small'
              label='Rolle'
              value={row.idLegalRole ?? ''}
              onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                onChange(row.idLegalClient, 'idLegalRole', Number(target.value))
              }
            >
              {legalRoles.map(role => (
                <MenuItem key={role.idLegalRole} value={role.idLegalRole}>
                  {role.LegalRole}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            '-'
          ),
      },
      {
        field: 'LegalClientGroups',
        headerName: 'Gruppen',
        flex: 1,
        valueGetter: ({ row }: GridValueGetterParams<ILegalClient>) =>
          row.LegalClientGroups && row.LegalClientGroups.length > 0
            ? row.LegalClientGroups.map(g => g.Name).join(', ')
            : '',
      },
      {
        field: 'loginPossible',
        headerName: 'Login',
        flex: 1,
        renderCell: ({ value }) => (Boolean(value) ? <Check /> : <Close />),
      },
      {
        field: 'isReceivingEmailEnabled',
        headerName: 'E-Mails',
        flex: 1,
        renderCell: ({ value }) => (Boolean(value) ? <Check /> : <Close />),
      },
      { field: 'FirstName', headerName: 'Vorname', flex: 1 },
      { field: 'LastName', headerName: 'Nachname', flex: 1 },
      { field: 'Email', headerName: 'E-Mail-Adresse', flex: 1 },
      { field: 'idArnoTopIntern', headerName: 'Namen.Nr.', flex: 1 },
      {
        field: 'enableInvoiceReminding',
        headerName: 'Erinnerung Rechnung',
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams<ILegalClient>) => (
          <Checkbox
            checked={row.enableInvoiceReminding}
            onChange={() =>
              onChange(
                row.idLegalClient,
                'enableInvoiceReminding',
                !row.enableInvoiceReminding
              )
            }
          />
        ),
      },
      {
        field: 'enableCertificateReminding',
        headerName: 'Erinnerung Urkunden',
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams<ILegalClient>) => (
          <Checkbox
            checked={row.enableCertificateReminding}
            onChange={() =>
              onChange(
                row.idLegalClient,
                'enableCertificateReminding',
                !row.enableCertificateReminding
              )
            }
          />
        ),
      },
      {
        field: 'Actions',
        headerName: 'Aktionen',
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams<ILegalClient>) => (
          <>
            <IconButton
              title='Mandanten öffnen'
              onClick={() =>
                window.open(`/clients/${row.idLegalClient}`, '_blank')
              }
            >
              <Link />
            </IconButton>
            <IconButton
              title='Mandanten Löschen'
              onClick={() => onRemove(row.idLegalClient)}
            >
              <Delete />
            </IconButton>
          </>
        ),
      },
    ],
    [legalRoles, onChange, onRemove]
  );

  return (
    <>
      {setLegalRoles && legalRoleCanInviteMap && setLegalRoleCanInviteMap && (
        <Box sx={{ mb: 2 }}>
          <LegalRoleEditDialog
            legalRoles={legalRoles}
            setLegalRoles={setLegalRoles}
            takenLegalClients={takenLegalClients}
            legalRoleCanInviteMap={legalRoleCanInviteMap}
            setLegalRoleCanInviteMap={setLegalRoleCanInviteMap}
          />
          <br />
        </Box>
      )}
      <Typography variant='h5'>
        Mandanten
        <IconButton
          onClick={() => setIsOpen(true)}
          sx={{ float: 'right', mt: 1, ml: 2 }}
        >
          <Add />
        </IconButton>
      </Typography>
      {/*
        #1283: Die Erreichbarkeits-Warnungen aus #1198 sind jetzt
        Health-Check-Regeln (`client.missing-email`, `client.no-portal-access`,
        `client.no-notification`) und werden ausschliesslich im Health Check
        angezeigt — nicht zusaetzlich hier, damit die Mandanten-Uebersicht
        nicht zulaeuft.
      */}
      <DataGrid
        sx={{ mt: 2 }}
        autoHeight
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        rows={takenLegalClients}
        columns={columns}
        getRowId={row => row.idLegalClient}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        disableRowSelectionOnClick
      />
      {isOpen && (
        <AssignLegalClientsDialog
          unmount={() => setIsOpen(false)}
          assignedLegalClients={takenLegalClients}
          setAssignedLegalClients={onTakeClients}
        />
      )}

      {idLegalTransaction && (
        <LegalClientsInvitedOverview idLegalTransaction={idLegalTransaction} />
      )}
    </>
  );
};
